import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trash2, ShieldAlert, RefreshCw } from "lucide-react";

const ENTITY_COLORS = {
  "İcra Kaydı": "#B8933F",
  "İcra Kaydı (Toplu)": "#B8933F",
  "Cari Kaydı": "#2F6E4E",
  "Cari Ödeme": "#2F6E4E",
  "Ön Onay Başvurusu": "#B8933F",
  "Sevkiyat": "#7C5E9B",
  "Kullanıcı": "#A63A3A",
};

function fmtDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AuditLog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit/deletions");
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kayıtlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [load, isAdmin]);

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="audit-no-access">
          <ShieldAlert className="w-10 h-10 text-[#A63A3A] mb-3" />
          <h2 className="font-heading text-xl font-bold">Yetkiniz Yok</h2>
          <p className="text-sm text-[#8C8377] mt-1">Bu sayfa yalnızca yöneticiler içindir.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">DENETİM</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Silme Kaydı</h1>
          <p className="text-sm text-[#8C8377] mt-1">Kim, neyi, ne zaman sildi.</p>
        </div>
        <button onClick={load} data-testid="audit-refresh" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">
          <RefreshCw className="w-4 h-4 text-[#B8933F]" /> Yenile
        </button>
      </div>

      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto max-h-[calc(100vh-14rem)]" data-testid="audit-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-[#F3ECDD]">
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              <th className="px-3 py-2.5 kicker text-left">Tarih / Saat</th>
              <th className="px-3 py-2.5 kicker text-left">Tür</th>
              <th className="px-3 py-2.5 kicker text-left">Silinen Kayıt</th>
              <th className="px-3 py-2.5 kicker text-left">Silen Kullanıcı</th>
              <th className="px-3 py-2.5 kicker text-right">Adet</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-3 py-10 text-center text-[#8C8377]">Yükleniyor...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-10 text-center text-[#8C8377]" data-testid="audit-empty">Henüz silme kaydı yok.</td></tr>
            ) : rows.map((r, i) => {
              const c = ENTITY_COLORS[r.entity] || "#8C8377";
              return (
                <tr key={r.id} className={`border-b border-[#E7DECC] hover:bg-[#EFE7D6] transition-colors duration-150 ${i % 2 ? "bg-[#F3ECDD]/60" : ""}`} data-testid={`audit-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums text-[#8C8377]">{fmtDateTime(r.at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold" style={{ backgroundColor: `${c}1a`, color: c }}>
                      <Trash2 className="w-3 h-3" /> {r.entity}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-medium">{r.label || "-"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="leading-tight">
                      <div className="font-medium">{r.by_name || "-"}</div>
                      <div className="text-xs text-[#8C8377]">{r.by_email}</div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.count || 1}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
